import React, { useState, useEffect } from "react";
import { BiFemale, BiMale } from "react-icons/bi";
import { FaRegPenToSquare } from "react-icons/fa6";
import { GiLovers } from "react-icons/gi";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import ReportSection from "../../../Components/ReportSection/ReportSection";
import { authorizedGet } from "../../../Config/networkWithToken";
import configuration from "../../../Config/configuration";
import {
  config,
  getValueFromLocalStorage,
  localStorageKey,
} from "../../../utils/helper";
import Spinner from "../../../Components/Spinner/Spinner";

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DashBoard = () => {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonthIndex = date.getMonth();
  const currentMonth = months[currentMonthIndex];
  const [loading, setLoading] = useState(false);
  const [filteredStats, setFilteredStats] = useState([]);
  const [totalUser, setTotalUser] = useState(false);
  const [sortBy, setSortBy] = useState("thisYear");
  const [startDate, setStartDate] = useState({ month: "Oct", year: 2020 });
  const [endDate, setEndDate] = useState({
    month: currentMonth,
    year: currentYear,
  });
  const [fullReportData, setFullReportData] = useState(null);
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");

  useEffect(() => {
    fetchReport();
  }, [sortBy, startDate, endDate]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let url = `${configuration.apis.getReport}?adminId=${adminId}`;

      if (sortBy === "thisYear") {
        url += `&date=this_year`;
      } else if (sortBy === "thisMonth") {
        url += `&date=this_month`;
      } else if (sortBy === "previousYear") {
        url += `&date=previous_year`;
      } else if (sortBy === "custom") {
        const getMonthIndex = (monthName) => months.indexOf(monthName);
        const start = new Date(
          startDate.year,
          getMonthIndex(startDate.month),
          1,
        );
        const isCurrentMonth =
          endDate.year === currentYear && endDate.month === currentMonth;
        const end = isCurrentMonth
          ? new Date()
          : new Date(endDate.year, getMonthIndex(endDate.month) + 1, 0);

        const formatDate = (date) => {
          const day = String(date.getDate()).padStart(2, "0");
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const year = date.getFullYear();
          return `${day}-${month}-${year}`;
        };

        url += `&dateFrom=${formatDate(start)}&dateTo=${formatDate(end)}`;
      }

      const res = await authorizedGet(url, config);
      const responseData = res?.data?.data?.UserRecord || [];
      setFullReportData(res?.data?.data);
      setTotalUser(res?.data?.data?.totalUsers)
      const formatted = responseData.map((item) => ({
        month: item.month || item.week || item.year || "Unknown",
        Registered: item.total || 0,
        Active: item.active || 0,
        ActiveMale: item.activeMale || 0,
        ActiveFemale: item.activeFemale || 0,
      }));

      setFilteredStats(formatted);
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  // Month filtering logic based on selected year
  const getAvailableMonths = (year) => {
    if (year === 2020) return months.slice(9); // Oct - Dec
    if (year === currentYear) return months.slice(0, currentMonthIndex + 1); // Jan to current month
    return months;
  };

  const cards = [
    {
      icon: <FaRegPenToSquare />,
      head: "Total Registrations",
      users: totalUser?.registerUsers ||0,
      bgColor: "bg-gradient-to-r from-yellow-400 to-yellow-600",
    },
    {
      icon: <GiLovers />,
      head: "Active Users",
      users: totalUser?.activeUsers ||0,
      bgColor: "bg-gradient-to-r from-green-400 to-green-600",
    },
    {
      icon: <BiMale />,
      head: "Active Male Users",
      users: totalUser?.activeFemaleUsers ||0,
      bgColor: "bg-gradient-to-r from-cyan-500 to-cyan-700",
    },
    {
      icon: <BiFemale />,
      head: "Active Female Users",
      users: totalUser?.activeMaleUsers ||0,
      bgColor: "bg-gradient-to-r from-pink-400 to-pink-600",
    },
  ];

  return (
    <div className="mx-auto w-[97%] pb-12">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((item, index) => (
          <div
            key={index}
            className={`flex flex-col items-center justify-center rounded-xl p-4 text-white ${item.bgColor}`}
          >
            <div className="mb-2 text-3xl">{item.icon}</div>
            <h3 className="text-lg font-semibold">{item.head}</h3>
            <p className="text-2xl font-bold">{item.users}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center  mt-4  justify-end">
            sort by:
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="rounded-lg border px-4 py-2 text-sm shadow-sm outline-none"
            >
              <option value="thisMonth">This Month ({currentMonth})</option>
              <option value="thisYear">This Year ({currentYear})</option>
              <option value="previousYear">
                Previous Year ({currentYear - 1})
              </option>
              <option value="custom">Custom Range</option>
            </select>

            {sortBy === "custom" && (
              <div className="flex flex-wrap items-center gap-2">
                {/* Start Date */}
                <div className="flex gap-1">
                  <select
                    value={startDate.month}
                    onChange={(e) =>
                      setStartDate({ ...startDate, month: e.target.value })
                    }
                    className="rounded border px-2 py-1 text-sm"
                  >
                    {getAvailableMonths(startDate.year).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={startDate.year}
                    onChange={(e) =>
                      setStartDate({
                        ...startDate,
                        year: parseInt(e.target.value),
                        month: getAvailableMonths(parseInt(e.target.value))[0],
                      })
                    }
                    className="rounded border px-2 py-1 text-sm"
                  >
                    {Array.from({ length: 6 }).map((_, idx) => {
                      const y = currentYear - 5 + idx;
                      return <option key={y}>{y}</option>;
                    })}
                  </select>
                </div>

                <span className="px-1 text-sm text-gray-600">to</span>

                {/* End Date */}
                <div className="flex gap-1">
                  <select
                    value={endDate.month}
                    onChange={(e) =>
                      setEndDate({ ...endDate, month: e.target.value })
                    }
                    className="rounded border px-2 py-1 text-sm"
                  >
                    {getAvailableMonths(endDate.year).map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                  <select
                    value={endDate.year}
                    onChange={(e) =>
                      setEndDate({
                        ...endDate,
                        year: parseInt(e.target.value),
                        month: getAvailableMonths(parseInt(e.target.value))[0],
                      })
                    }
                    className="rounded border px-2 py-1 text-sm"
                  >
                    {Array.from({ length: 6 }).map((_, idx) => {
                      const y = currentYear - 5 + idx;
                      return <option key={y}>{y}</option>;
                    })}
                  </select>
                </div>
              </div>
            )}
          </div>
      <div className="mt-12 rounded-2xl bg-white p-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              User Growth & Engagement
            </h2>
            <p className="text-sm text-gray-500">Overview of user activity</p>
          </div>

         
        </div>

        {/* Chart */}
        <div className="h-96 w-full">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={filteredStats}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <defs>
                  <linearGradient
                    id="colorRegistered"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorActive" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorActiveMale"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorActiveFemale"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#ec4899" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#ec4899" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <XAxis dataKey="month" />
                <YAxis />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip />
                <Legend />

                <Area
                  type="monotone"
                  dataKey="Registered"
                  stroke="#f59e0b"
                  fillOpacity={1}
                  fill="url(#colorRegistered)"
                  dot={{
                    r: 4,
                    stroke: "#f59e0b",
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="Active"
                  stroke="#22c55e"
                  fillOpacity={1}
                  fill="url(#colorActive)"
                  dot={{
                    r: 4,
                    stroke: "#22c55e",
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="ActiveMale"
                  stroke="#06b6d4"
                  fillOpacity={1}
                  fill="url(#colorActiveMale)"
                  dot={{
                    r: 4,
                    stroke: "#06b6d4",
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="ActiveFemale"
                  stroke="#ec4899"
                  fillOpacity={1}
                  fill="url(#colorActiveFemale)"
                  dot={{
                    r: 4,
                    stroke: "#ec4899",
                    strokeWidth: 2,
                    fill: "white",
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
      <ReportSection reportData={fullReportData} />
    </div>
  );
};

export default DashBoard;
